import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { ArrowRight, Mail, Lock, User, Eye, EyeOff, ShieldCheck, FileText, Shield, CheckCircle, ClipboardList, FlaskConical, ScanLine, CreditCard, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth, API } from "../App";
import LanguageSwitcher from "../components/LanguageSwitcher";
import BrandLogo from "../components/BrandLogo";

const clinicalShadow = "0 4px 20px rgba(0,101,141,0.06)";
const btnGradient = "linear-gradient(180deg, #00aeef 0%, #00658d 100%)";

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "", age_confirmed: false });
  const [twoFactorState, setTwoFactorState] = useState({ active: false, token: "", email: "", code: "", resending: false, resendCooldown: 0 });

  // Hydrating 2FA + UI flag state from router location.state on mount — the
  // correct pattern; eslint disable scoped to these intentional setState calls.
  React.useEffect(() => {
    if (location.state?.fromGoogle && location.state?.twoFactorToken) {
      setShowAuth(true);
      setTwoFactorState({
        active: true, token: location.state.twoFactorToken,
        email: location.state.twoFactorEmail, code: "", resending: false, resendCooldown: 0,
      });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  React.useEffect(() => {
    // Skip redirect if a 2FA challenge is in progress (just returned from
    // Google OAuth) — both via React state and the freshly-arrived router
    // location.state, since both effects fire on the same render and the
    // 2FA state setter has not yet been committed when this effect runs.
    const pendingGoogle2FA = location.state?.fromGoogle && location.state?.twoFactorToken;
    if (user && !twoFactorState.active && !pendingGoogle2FA) {
      navigate((user.is_doctor) ? "/doctor/dashboard" : "/dashboard");
    }
  }, [user, navigate, twoFactorState.active, location.state]);

  React.useEffect(() => {
    if (twoFactorState.resendCooldown <= 0) return;
    const timer = setTimeout(() => {
      setTwoFactorState(prev => ({ ...prev, resendCooldown: prev.resendCooldown - 1 }));
    }, 1000);
    return () => clearTimeout(timer);
  }, [twoFactorState.resendCooldown]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      if (response.data.requires_2fa) {
        setTwoFactorState({ active: true, token: response.data.two_factor_token, email: response.data.email, code: "", resending: false, resendCooldown: 0 });
        if (response.data.code_sent === false) {
          setError(t('auth.codeSendFailed'));
        }
        setLoading(false);
        return;
      }
      login(response.data.user, response.data.token);
      setTimeout(() => navigate((response.data.user.is_doctor) ? "/doctor/dashboard" : "/dashboard"), 100);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.loginFailed'));
    }
    setLoading(false);
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, { two_factor_token: twoFactorState.token, code: twoFactorState.code });
      login(response.data.user, response.data.token);
      setTimeout(() => navigate((response.data.user.is_doctor) ? "/doctor/dashboard" : "/dashboard"), 100);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.verificationFailed'));
    }
    setLoading(false);
  };

  const handleResend2FA = async () => {
    setTwoFactorState(prev => ({ ...prev, resending: true }));
    setError("");
    try {
      await axios.post(`${API}/auth/2fa/resend`, { two_factor_token: twoFactorState.token });
      setTwoFactorState(prev => ({ ...prev, resending: false, resendCooldown: 60, code: "" }));
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.resendFailed'));
      setTwoFactorState(prev => ({ ...prev, resending: false }));
    }
  };

  const handleBack2FA = () => {
    setTwoFactorState({ active: false, token: "", email: "", code: "", resending: false, resendCooldown: 0 });
    setError("");
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/register`, registerForm);
      login(response.data.user, response.data.token);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.registerFailed'));
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // Clear any stale auth state so a previous user does not skip the 2FA
    // challenge issued for this fresh OAuth session.
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("justLoggedIn");
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Inter', sans-serif", background: "#f7f9fe", color: "#181c20" }}>
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-lg" style={{ background: "rgba(255,255,255,0.8)", boxShadow: "0 1px 12px rgba(0,101,141,0.05)" }}>
        <div className="max-w-7xl mx-auto flex justify-between items-center px-8 py-4">
          <div className="flex items-center gap-8">
            <Link to="/" data-testid="header-brand" className="transition-opacity hover:opacity-80">
              <BrandLogo variant="header" title={t('landing.title')} />
            </Link>
            <nav className="hidden md:flex gap-6 items-center">
              <Link
                to="/blog"
                data-testid="header-blog-link"
                className="text-sm tracking-tight transition-all hover:opacity-80"
                style={{ color: "#64748b" }}
              >
                {t("blog.nav")}
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-6">
            <LanguageSwitcher />
            <button
              data-testid="header-for-doctors-btn"
              onClick={() => navigate('/doctor')}
              className="text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-all"
              style={{ background: btnGradient, boxShadow: "0 4px 16px rgba(0,101,141,0.2)" }}
            >
              {t('landing.forDoctors')}
            </button>
          </div>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-8 py-20 flex flex-col md:flex-row items-center gap-16">
          <div className="flex-1 space-y-8">
            <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[1.05]">
              {t('landing.heroTitle') || "Kilpnaarme"}{" "}
              <span
                className="block text-3xl md:text-4xl mt-4 font-extrabold"
                style={{ color: "#00658D" }}
              >
                {t('landing.heroHighlight') || "analuus"}
              </span>
            </h1>
            <p className="text-lg max-w-xl leading-relaxed font-bold" style={{ color: "#3e4850" }}>
              {t('landing.description')}
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="hero-get-started-btn"
                onClick={() => setShowAuth(true)}
                className="px-8 py-4 text-white rounded-full font-bold text-lg hover:scale-[1.02] transition-transform"
                style={{ background: btnGradient, boxShadow: "0 12px 24px rgba(0,101,141,0.25)" }}
              >
                {t('landing.forPatients') || "Patsientidele"}
              </button>
              <p className="mt-3 text-sm inline-flex items-center gap-2" style={{ color: "#6e7881" }} data-testid="landing-estonia-only">
                <MapPin className="w-4 h-4" style={{ color: "#00658D" }} />
                {t('common.estoniaOnlyNote') || 'Teenus on saadaval ainult Eestis.'}
              </p>
            </div>
          </div>
          <div className="flex-1 relative">
            <div className="absolute -inset-4 rounded-[40px] blur-3xl" style={{ background: "rgba(0,101,141,0.05)" }} />
            <img
              src="/brand/hero.webp"
              alt="Doctor performing thyroid neck examination on a patient at Kilpnäärme Kliinik"
              className="relative rounded-3xl w-full object-cover shadow-2xl"
              style={{ border: "1px solid rgba(255,255,255,0.2)", aspectRatio: "16 / 10" }}
              width="1376"
              height="768"
              fetchPriority="high"
              decoding="async"
              data-testid="landing-hero-image"
            />
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-24" style={{ background: "#f1f4f9" }}>
          <div className="max-w-7xl mx-auto px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">{t('landing.howItWorks')}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                { step: "01", titleKey: "landing.step1Title", descKey: "landing.step1Desc", Icon: ClipboardList },
                { step: "02", titleKey: "landing.step2Title", descKey: "landing.step2Desc", Icon: FlaskConical },
                { step: "03", titleKey: "landing.step3Title", descKey: "landing.step3Desc", Icon: ScanLine },
                { step: "04", titleKey: "landing.step4Title", descKey: "landing.step4Desc", Icon: CreditCard },
                { step: "05", titleKey: "landing.step5Title", descKey: "landing.step5Desc", Icon: CheckCircle },
              ].map(({ step, titleKey, descKey, Icon }) => (
                <div key={step} className="p-6 rounded-3xl space-y-3 hover:shadow-xl transition-all" style={{ background: "#fff", boxShadow: clinicalShadow }} data-testid={`landing-step-${step}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(198,231,255,0.4)" }}>
                    <Icon className="w-6 h-6" style={{ color: "#00658D" }} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#00658D" }}>
                    {t('landing.step') || "Samm"} {step}
                  </span>
                  <h3 className="text-lg font-bold leading-tight">{t(titleKey)}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#3e4850" }}>{t(descKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="pricing" className="max-w-7xl mx-auto px-8 mb-24">
          <div
            className="relative overflow-hidden rounded-[3rem] p-12 md:p-20 text-center text-white"
            style={{ background: btnGradient, boxShadow: "0 24px 48px rgba(0,101,141,0.3)" }}
          >
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full -mr-20 -mt-20 blur-3xl" style={{ background: "rgba(255,255,255,0.1)" }} />
            <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full -ml-20 -mb-20 blur-3xl" style={{ background: "rgba(0,0,0,0.1)" }} />
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 relative z-10 max-w-3xl mx-auto leading-tight whitespace-pre-line">
              {t('landing.ctaTitle')}
            </h2>
            <p className="text-lg md:text-xl italic mb-12 relative z-10 max-w-xl mx-auto" style={{ color: "rgba(255,255,255,0.9)" }}>
              — {t('landing.ctaAttribution')}
            </p>
            <div className="relative z-10">
              <button
                data-testid="cta-get-started-btn"
                onClick={() => setShowAuth(true)}
                className="px-12 py-5 rounded-full font-bold text-xl hover:scale-105 transition-transform"
                style={{ background: "#fff", color: "#00658D", boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
              >
                {t('landing.forPatients') || "Patsientidele"}
              </button>
            </div>
          </div>
        </section>

        {/* Korduma Kippuvad Küsimused (KKK) — matches FAQPage JSON-LD in index.html.
            Google requires the visible page to contain the same questions
            and answers for FAQPage rich results to appear. */}
        <section className="w-full py-16 px-8 pb-40 md:pb-20" style={{ background: "#fff" }} data-testid="landing-faq-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-2 text-center" style={{ color: "#00658D" }}>
              {t("landing.faqTitle") || "Korduma Kippuvad Küsimused"}
            </h2>
            <p className="text-base text-center mb-10" style={{ color: "#64748b" }}>
              {t("landing.faqSubtitle") || "Vastused kõige sagedasematele küsimustele kilpnäärme analüüsi kohta."}
            </p>
            <div className="space-y-3">
              {[
                {
                  q: t("landing.faqQ1") || "Kellele see kilpnäärme analüüs on mõeldud?",
                  a: t("landing.faqA1") || "Kilpnäärme Kliiniku analüüs on mõeldud vähemalt 18-aastastele Eestis viibivatele täiskasvanutele, kellel on olemas laboratoorsete analüüside vastused (vähemalt TSH). Kõik lõplikud raportid vaatab enne patsiendile edastamist üle endokrinoloog Verner Fogel.",
                },
                {
                  q: t("landing.faqQ2") || "Kui palju maksab kilpnäärme analüüsi raport?",
                  a: t("landing.faqA2") || "Ühekordne kilpnäärme analüüsi raport patsientidele maksab 20 eurot ja sisaldab endokrinoloogi kinnitust enne raporti kättesaamist. Maksmine toimub Stripe kaudu (turvaline kaardimakse).",
                },
                {
                  q: t("landing.faqQ3") || "Kes vaatab AI raporti üle?",
                  a: t("landing.faqA3") || "Kõik raportid vaatab enne patsiendile edastamist üle sertifitseeritud endokrinoloog Verner Fogel. AI mustand on ainult tööriist — kliiniline hinnang ja allkiri tuleb arstilt. Patsient saab raporti alles pärast arsti kinnitust (tavaliselt 24–48 tunni jooksul).",
                },
                {
                  q: t("landing.faqQ4") || "Milliseid laborianalüüse saab sisestada?",
                  a: t("landing.faqA4") || "Süsteem võtab vastu TSH, vaba T4 (fT4), vaba T3 (fT3), aTPO, aTG, TSI/TRAb, kaltsitoniin ning verepilti (leukotsüüdid, neutrofiilid, lümfotsüüdid, monotsüüdid). Lisaks saab üles laadida kilpnäärme ultraheliuuringu vastuse (sh TIRADS klassifikatsioon).",
                },
                {
                  q: t("landing.faqQ5") || "Kas mu andmed on GDPR-i kohaselt kaitstud?",
                  a: t("landing.faqA5") || "Jah. Kilpnäärme Kliinik järgib GDPR-i (EL 2016/679). Iga nõustumine (meditsiiniline vastutus, privaatsuspoliitika, 18+ vanuse kinnitus) salvestatakse audit-logisse Art. 7 kohaselt. Patsient saab igal ajal oma andmed eksportida (JSON) või täielikult kustutada 'Minu andmed' menüüst.",
                },
              ].map((item, idx) => (
                <details
                  key={idx}
                  className="rounded-2xl border overflow-hidden transition-all"
                  style={{ borderColor: "#e2e8f0", background: "#f8fafc" }}
                  data-testid={`landing-faq-item-${idx}`}
                >
                  <summary
                    className="cursor-pointer select-none px-6 py-4 font-semibold text-base flex items-center justify-between"
                    style={{ color: "#00658D" }}
                  >
                    <span>{item.q}</span>
                    <span className="text-2xl leading-none ml-4" style={{ color: "#94a3b8" }}>+</span>
                  </summary>
                  <div className="px-6 pb-5 text-sm leading-relaxed" style={{ color: "#334155" }}>
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="w-full py-12 px-8" style={{ background: "#f8fafc" }}>
        <div className="max-w-7xl mx-auto">
          <div className="h-px mb-8" style={{ background: "#e2e8f0" }} />
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <BrandLogo variant="footer" title={t('landing.title')} />
              </div>
              <p className="text-sm" style={{ color: "#64748b" }}>Reveron Baltic OÜ · Registrikood 11527272 · {t('landing.title')}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-8">
              <Link to="/blog" className="text-sm transition-all hover:underline" style={{ color: "#64748b" }} data-testid="footer-blog-link">{t("blog.nav")}</Link>
              <Link to="/privacy" className="text-sm transition-all hover:underline" style={{ color: "#64748b" }} data-testid="footer-privacy-link">{t("common.privacyPolicy")}</Link>
              <Link to="/disclaimer" className="text-sm transition-all hover:underline" style={{ color: "#64748b" }} data-testid="footer-disclaimer-link">{t("common.medicalDisclaimer")}</Link>
              <a href="mailto:support@kilpnäärmekliinik.ee" className="text-sm transition-all hover:underline" style={{ color: "#64748b" }}>{t("blog.contact")}</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Modal - preserved from original */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md" data-testid="auth-modal" style={{ fontFamily: "'Inter', sans-serif" }}>
            <CardHeader className="text-center pb-2">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 overflow-hidden" style={{ background: "#ffffff", border: "1px solid #e2e8f0" }}>
                <img src="/brand/logo.webp" alt="Kilpnäärme Kliinik" className="w-12 h-12 object-contain" />
              </div>
              <CardTitle className="text-2xl">{t('landing.welcome')}</CardTitle>
              <CardDescription>{t('landing.loginPrompt')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button data-testid="google-login-btn" onClick={handleGoogleLogin} variant="outline" className="w-full mb-4 py-6 border-slate-200 hover:bg-slate-50">
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.googleButtonText')}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-slate-500">{t('auth.orContinueWith')}</span></div>
              </div>

              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="login" data-testid="login-tab">{t('auth.loginTitle')}</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">{t('auth.registerTitle')}</TabsTrigger>
                </TabsList>

                {error && <div className="text-sm p-3 rounded-lg mb-4" style={{ background: "rgba(141,79,0,0.08)", color: "#6d3a00" }}>{error}</div>}

                <TabsContent value="login">
                  {twoFactorState.active ? (
                    <div className="space-y-5" data-testid="patient-2fa-section">
                      <div className="text-center">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(198,231,255,0.4)" }}>
                          <ShieldCheck className="w-7 h-7" style={{ color: "#00658D" }} />
                        </div>
                        <h2 className="text-lg font-bold mb-1">{t('auth.verifyIdentity') || 'Verify Your Identity'}</h2>
                        <p className="text-sm text-slate-500">{t('auth.codeSentTo') || 'A 6-digit code has been sent to'}</p>
                        <p className="text-sm font-semibold text-slate-700 mt-1">{twoFactorState.email}</p>
                      </div>
                      <form onSubmit={handleVerify2FA} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="patient-2fa-code">{t('auth.verificationCode') || 'Verification Code'}</Label>
                          <Input id="patient-2fa-code" data-testid="patient-2fa-code-input" type="text" inputMode="numeric" maxLength={6} placeholder="000000"
                            className="text-center text-2xl tracking-[0.5em] font-mono h-14"
                            value={twoFactorState.code}
                            onChange={(e) => { const val = e.target.value.replace(/\D/g, '').slice(0, 6); setTwoFactorState(prev => ({ ...prev, code: val })); }}
                            autoFocus />
                        </div>
                        <Button type="submit" data-testid="patient-2fa-verify-btn" disabled={loading || twoFactorState.code.length !== 6}
                          className="w-full text-white h-12 rounded-xl" style={{ background: "#00658D" }}>
                          {loading ? <div className="spinner" /> : (t('auth.verify') || 'Verify')}
                        </Button>
                      </form>
                      <div className="flex items-center justify-between pt-1">
                        <button type="button" onClick={handleBack2FA} className="text-sm text-slate-500 hover:text-slate-700" data-testid="patient-2fa-back-btn">
                          {"\u2190"} {t('auth.backToLogin') || 'Back to Login'}
                        </button>
                        <button type="button" onClick={handleResend2FA} disabled={twoFactorState.resending || twoFactorState.resendCooldown > 0}
                          className="text-sm font-medium disabled:opacity-50" style={{ color: "#00658D" }} data-testid="patient-2fa-resend-btn">
                          {twoFactorState.resendCooldown > 0 ? `${t('auth.resendIn') || 'Resend in'} ${twoFactorState.resendCooldown}s` : (t('auth.resendCode') || 'Resend code')}
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 text-center">{t('auth.codeExpiry') || 'Code expires in 5 minutes'}</p>
                    </div>
                  ) : (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label htmlFor="login-email">{t('common.email')}</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input id="login-email" data-testid="login-email-input" type="email" placeholder={t('auth.emailPlaceholder')} className="pl-10"
                            value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} required />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="login-password">{t('common.password')}</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input id="login-password" data-testid="login-password-input" type={showPassword ? "text" : "password"} placeholder={t('auth.passwordPlaceholder')} className="pl-10 pr-10"
                            value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} required />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <Button data-testid="login-submit-btn" type="submit" className="w-full text-white" style={{ background: "#00658D" }} disabled={loading}>
                        {loading ? <div className="spinner" /> : t('auth.loginButton')}
                      </Button>
                    </form>
                  )}
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="register-name">{t('common.name')}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input id="register-name" data-testid="register-name-input" type="text" placeholder={t('auth.namePlaceholder')} className="pl-10"
                          value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })} required />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="register-email">{t('common.email')}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input id="register-email" data-testid="register-email-input" type="email" placeholder={t('auth.emailPlaceholder')} className="pl-10"
                          value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} required />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="register-password">{t('common.password')}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input id="register-password" data-testid="register-password-input" type={showPassword ? "text" : "password"} placeholder={t('auth.createPassword')} className="pl-10 pr-10"
                          value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} required />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg" style={{ background: "#f7f9fe", border: "1px solid rgba(189,200,209,0.3)" }}>
                        <input
                          type="checkbox"
                          checked={registerForm.age_confirmed}
                          onChange={(e) => setRegisterForm({ ...registerForm, age_confirmed: e.target.checked })}
                          className="mt-0.5 w-4 h-4 rounded"
                          style={{ accentColor: "#00658D" }}
                          data-testid="register-age-checkbox"
                          required
                        />
                        <span className="text-sm" style={{ color: "#3e4850" }}>
                          {t('auth.ageConfirmLabel') || 'Kinnitan, et olen vähemalt 18-aastane ning nõustun Privaatsuspoliitika ja Meditsiinilise vastutuse välistamisega.'}
                        </span>
                      </label>
                    </div>
                    <Button data-testid="register-submit-btn" type="submit" className="w-full text-white" style={{ background: "#00658D" }} disabled={loading || !registerForm.age_confirmed}>
                      {loading ? <div className="spinner" /> : t('auth.registerButton')}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              <button data-testid="close-auth-modal" onClick={() => setShowAuth(false)} className="w-full mt-4 text-sm text-slate-500 hover:text-slate-700">
                {t('common.cancel')}
              </button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
